import React, { useState, useEffect } from "react";

import { useTranslation, Trans } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import { isEmpty } from "lodash";
import Select from "react-select";

import StepsNavButtons from "../Buttons/StepsNavButtons";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import TitleWithDescription from "../Headers/TitleWithDescription";
import ProductTags from "../Tags/ProductTags";
import DraggableProductTags from "../Tags/DraggableProductTags";
import DragDropUploadCard from "../Cards/DragDropUploadCard";
import PublishModelSchema from "../../schemas/publishModel.schema";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import { BOOLEAN_DROPDOWN, MODEL_SCOPE } from "../../utils/constants";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import EditableImage from "../../components/Images/EditableImages";
import { uniqueProductVersionName } from "../../utils/UniqueVersionNameHelpers";
import InformationTooltip from "../Tooltips/InformationTooltip";

const MAX_FILE_NUMBER = 4;
const IMAGE_TYPE = "image/jpeg, image/jpg, image/png";

const ManageModel = ({
    nextStepButtonText,
    nextStep,
    prevStep,
    manageModelForm,
    setManageModelForm,
    selectedModel,
    selectedModelMaterial,
    selectedParameterVersion,
    supportedAccessories,
    countries,
    industries,
    scannerTypes,
    tags,
    productImages,
    setProductImages,
    coverImage,
    setCoverImage,
    productGuide,
    setProductGuide,
    isBundle,
    selectedProductsMaterials,
    isNewVersion,
    productId,
    product,
    isUpdate,
    isForceUpgradeEnabled,
}) => {
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});

    const [instructionsInputVal, setInstructionsInputVal] = useState("");
    const [whatsNewInputVal, setWhatsNewInputVal] = useState("");

    const { t } = useTranslation();

    const universalModelOptions = [
        {
            name: MODEL_SCOPE.local,
            label: t("local"),
            value: MODEL_SCOPE.local,
        },
        {
            name: MODEL_SCOPE.global,
            label: t("global"),
            value: MODEL_SCOPE.global,
        },
    ];

    useEffect(() => {
        if (!isBundle) {
            setManageModelForm((prevState) => {
                return {
                    ...prevState,
                    productName: prevState.productName || selectedModel.label,
                    materialName:
                        prevState.materialName ||
                        selectedModelMaterial.materialName ||
                        "",
                };
            });
        }
    }, []);

    useEffect(() => {
        handleImagesChange(productImages, "images");
    }, [productImages]);

    useEffect(() => {
        handleImagesChange(coverImage, "coverImage");
    }, [coverImage]);

    useEffect(() => {
        handleFileChange(productGuide, "productGuideURI");
    }, [productGuide]);

    useEffect(() => {
        if (!isForceUpgradeEnabled)
            setManageModelForm((prev) => ({
                ...prev,
                forceUpgrade: BOOLEAN_DROPDOWN.false.label,
            }));
    }, [isForceUpgradeEnabled]);

    useEffect(() => {
        validate(
            manageModelForm,
            PublishModelSchema.ManageModelForm,
            setIsFormValid,
            setError,
            {
                context: {
                    isBundle: !!isBundle,
                    isNewVersion: !!isNewVersion,
                    isUniversalModel:
                        !!manageModelForm?.universalModel?.booleanValue,
                },
            }
        );
    }, [manageModelForm]);

    const handleNextPage = async () => {
        setIsFormSubmitted(true);

        const isSameVersionName =
            manageModelForm.productVersion === product.version && isUpdate;

        const isVersionNameUnique =
            isSameVersionName ||
            (isFormValid &&
                isNewVersion &&
                !(await uniqueProductVersionName(
                    setError,
                    setIsFormValid,
                    productId,
                    manageModelForm.productVersion,
                    "productVersion"
                )));

        if (isFormValid && (!isNewVersion || isVersionNameUnique)) {
            nextStep();
        }
    };

    const handleInputChange = (e, isBool = false) => {
        setManageModelForm((prevState) => {
            const val = isBool ? e.target.value === "true" : e.target.value;
            return { ...prevState, [e.target.name]: val };
        });
    };

    const handleMultiSelect = (newValue, action) => {
        let newPropertyVals = [...manageModelForm[action.name]];
        switch (action.action) {
            case "select-option":
                newPropertyVals.push(action.option);
                break;
            case "remove-value":
                newPropertyVals = newPropertyVals.filter(
                    (unit) => unit.value !== action.removedValue.value
                );
                break;
            case "clear":
                newPropertyVals = [];
        }

        setManageModelForm((prevForm) => {
            return {
                ...prevForm,
                [action.name]: [...newPropertyVals],
            };
        });
    };

    const handleIsCreatableSelect = (newValue, action) => {
        let newPropertyVals = [...manageModelForm[action.name]];
        switch (action.action) {
            case "create-option":
                if (action.option.label.trim()) {
                    newPropertyVals.push({
                        ...action.option,
                        name: action.option.label,
                        isCreated: true,
                    });
                }
                break;
            case "select-option":
                newPropertyVals.push({
                    ...action.option,
                    id: action.option.value,
                });
                break;
            case "remove-value":
                newPropertyVals = newPropertyVals.filter(
                    (unit) => unit.value !== action.removedValue.value
                );
                break;
            case "clear":
                newPropertyVals = [];
        }

        setManageModelForm((prevForm) => {
            return {
                ...prevForm,
                [action.name]: [...newPropertyVals],
            };
        });
    };

    const handleFileChange = (file, propName) => {
        setManageModelForm((prevForm) => {
            return {
                ...prevForm,
                [propName]: !isEmpty(file)
                    ? file.data
                    : typeof file === "string"
                    ? file
                    : "",
            };
        });
    };

    const handleImagesChange = (files, propName) => {
        setManageModelForm((prevForm) => {
            return {
                ...prevForm,
                [propName]: files.map(
                    (file) => file.preview || file.image || file
                ),
            };
        });
    };

    const handleBooleanRadioChange = (e, trueMatch) => {
        setManageModelForm((prevForm) => {
            return {
                ...prevForm,
                [e.target.name]: {
                    value: e.target.value,
                    booleanValue: e.target.value === trueMatch,
                    label: BOOLEAN_DROPDOWN[e.target.value]?.label,
                },
            };
        });
    };

    const appendCard = (propName, currentInput, currentInputSetter) => {
        if (currentInput && currentInput.trim()) {
            setManageModelForm((prevForm) => {
                return {
                    ...prevForm,
                    [propName]: [
                        ...manageModelForm[propName],
                        currentInput.trim(),
                    ],
                };
            });
            currentInputSetter("");
        }
    };

    const removeCard = (index, propName, currentInputSetter) => {
        currentInputSetter(manageModelForm[propName][index]);

        setManageModelForm((prevForm) => {
            const sliced = [...manageModelForm[propName]];
            sliced.splice(index, 1);

            return {
                ...prevForm,
                [propName]: sliced,
            };
        });
    };

    const renderFields = (inputFields) => {
        return (
            inputFields &&
            inputFields.map((field, index) => {
                if (!field.hide) {
                    const inputGroup = field.element();
                    if (field.isArray) {
                        return (
                            <div
                                className="flex w-full justify-between"
                                key={index}
                            >
                                {inputGroup &&
                                    inputGroup.map(
                                        (innerFields, innerIndex) => {
                                            return (
                                                <div
                                                    className="tags input-group w-full"
                                                    key={`${index}-${innerIndex}`}
                                                >
                                                    <h4
                                                        className={`uppercase text-xs ${
                                                            innerFields.isRequired &&
                                                            "required"
                                                        }`}
                                                    >
                                                        {innerFields.label}
                                                        {innerFields.tooltip && (
                                                            <InformationTooltip
                                                                message={
                                                                    innerFields.tooltip
                                                                }
                                                            />
                                                        )}
                                                    </h4>
                                                    {innerFields.input}
                                                </div>
                                            );
                                        }
                                    )}
                            </div>
                        );
                    } else {
                        return (
                            <div className="tags input-group" key={index}>
                                <h4
                                    className={`uppercase text-xs ${
                                        inputGroup.isRequired && "required"
                                    }`}
                                >
                                    {inputGroup.label}
                                    {inputGroup.tooltip && (
                                        <InformationTooltip
                                            message={inputGroup.tooltip}
                                            className="ml-1"
                                        />
                                    )}
                                </h4>

                                {inputGroup.input}
                            </div>
                        );
                    }
                }
            })
        );
    };

    const handleFileUpload = (e) => {
        if (e.target.files && e.target.files[0]) {
            let reader = new FileReader();
            const file = e.target.files[0];

            reader.onload = (event) => {
                setProductGuide(
                    Object.assign(file, {
                        data: event.target.result,
                    })
                );
            };

            reader.readAsDataURL(file);
        } else {
            setProductGuide([e.target.value]);
        }
    };

    const resetLink = () => {
        return (
            <span
                onClick={() => setProductGuide({})}
                className="hyperlink text-xs cursor-pointer"
            >
                <Trans i18nKey="reset">Reset</Trans>
            </span>
        );
    };

    const modelInformationFields = [
        {
            element: () => ({
                label: isBundle ? (
                    <Trans i18nKey="bundleName">Bundle Name</Trans>
                ) : (
                    <Trans i18nKey="productName">Product Name</Trans>
                ),
                input: (
                    <div
                        className={`input-group ${
                            error.productName && isFormSubmitted
                                ? "invalid-warning-input"
                                : ""
                        }`}
                    >
                        <input
                            type="text"
                            name="productName"
                            value={manageModelForm.productName}
                            onChange={handleInputChange}
                            className="input-element"
                            placeholder={t("modelNamePlaceholder")}
                        />
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error.productName} />
                        )}
                    </div>
                ),
                isRequired: true,
            }),
        },
        {
            element: () => ({
                isRequired: true,
                label: <Trans i18nKey="material">Material</Trans>,
                input: isBundle ? (
                    <ProductTags
                        tags={[...new Set(selectedProductsMaterials)]}
                    />
                ) : (
                    <>
                        <div
                            className={`input-group ${
                                error.materialImage && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            } items-center w-1/12`}
                        >
                            <EditableImage
                                state={manageModelForm}
                                stateSetter={setManageModelForm}
                                attrName="materialImage"
                                defaultImage={
                                    require("../../assets/images/material.png")
                                        .default
                                }
                                className="my-1"
                            />
                        </div>

                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={error.materialImage}
                            />
                        )}
                    </>
                ),
            }),
        },
        {
            hide: isBundle,
            element: () => ({
                isRequired: true,
                label: <Trans i18nKey="materialName">Material Name</Trans>,
                input: (
                    <div
                        className={`input-group ${
                            error.materialName && isFormSubmitted
                                ? "invalid-warning-input"
                                : ""
                        }`}
                    >
                        <input
                            type="text"
                            name="materialName"
                            value={manageModelForm.materialName}
                            onChange={handleInputChange}
                            className="input-element"
                            placeholder={t("materialNamePlaceholder")}
                        />
                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={error.materialName}
                            />
                        )}
                    </div>
                ),
            }),
        },
        {
            element: () => {
                return {
                    label: isBundle ? (
                        <Trans i18nKey="bundleDescription">
                            Bundle Description
                        </Trans>
                    ) : (
                        <Trans i18nKey="productDescription">
                            Product Description
                        </Trans>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.description && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <textarea
                                name="description"
                                onChange={handleInputChange}
                                value={manageModelForm.description}
                                className="input-element textarea-element"
                                placeholder={t("descriptionPlaceholder")}
                            ></textarea>
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.description}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            hide: isBundle,
            element: () => {
                return {
                    label: <Trans i18nKey="parameters">Parameters</Trans>,
                    input: <ProductTags tags={selectedParameterVersion} />,
                };
            },
        },
        {
            isArray: true,
            element: () => {
                return [
                    {
                        label: (
                            <Trans i18nKey="compatibleWith">
                                Compatible With
                            </Trans>
                        ),
                        input: (
                            <div
                                className={`input-group ${
                                    error.compatibleWith && isFormSubmitted
                                        ? "invalid-warning-input"
                                        : ""
                                }`}
                            >
                                <Select
                                    isMulti
                                    options={scannerTypes}
                                    styles={getReactSelectStyling({
                                        showError:
                                            error.compatibleWith &&
                                            isFormSubmitted,
                                        isMultiSelect: true,
                                    })}
                                    value={manageModelForm.compatibleWith}
                                    onChange={handleMultiSelect}
                                    placeholder={t("selectPlaceholder", {
                                        entity: "Compatible With",
                                    })}
                                    name="compatibleWith"
                                    className="stacked-input-element"
                                    theme={getReactSelectTheme}
                                />
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={error.compatibleWith}
                                    />
                                )}
                            </div>
                        ),
                        isRequired: true,
                    },
                    {
                        label: (
                            <Trans i18nKey="supportedAccessories">
                                Supported Accessories
                            </Trans>
                        ),
                        input: (
                            <div
                                className={`input-group ${
                                    error.supportedAccessories &&
                                    isFormSubmitted
                                        ? "invalid-warning-input"
                                        : ""
                                }`}
                            >
                                <Select
                                    isMulti
                                    options={supportedAccessories}
                                    onChange={handleMultiSelect}
                                    value={manageModelForm.supportedAccessories}
                                    styles={getReactSelectStyling({
                                        showError:
                                            error.supportedAccessories &&
                                            isFormSubmitted,
                                        isMultiSelect: true,
                                    })}
                                    placeholder={t("selectPlaceholder", {
                                        entity: "Supported Accessories",
                                    })}
                                    name="supportedAccessories"
                                    theme={getReactSelectTheme}
                                />
                                {isFormSubmitted && (
                                    <ValidationError
                                        errorMessage={
                                            error.supportedAccessories
                                        }
                                    />
                                )}
                            </div>
                        ),
                    },
                ];
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="industries">Industries</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.industries && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <CreatableSelect
                                isMulti
                                options={industries}
                                styles={getReactSelectStyling({
                                    showError:
                                        error.industries && isFormSubmitted,
                                    isMultiSelect: true,
                                })}
                                value={manageModelForm.industries}
                                onChange={handleIsCreatableSelect}
                                placeholder={t("selectPlaceholder", {
                                    entity: "Industries",
                                })}
                                name="industries"
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.industries}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="tags">Tags</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.tags && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <CreatableSelect
                                isMulti
                                options={tags}
                                value={manageModelForm.tags}
                                styles={getReactSelectStyling({
                                    showError: error.tags && isFormSubmitted,
                                    isMultiSelect: true,
                                })}
                                onChange={handleIsCreatableSelect}
                                placeholder={t("selectPlaceholder", {
                                    entity: "Tags",
                                })}
                                name="tags"
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.tags} />
                            )}
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="instructions">Instructions</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.instructions && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <div className="flex mb-1">
                                <input
                                    type="text"
                                    name="instructions"
                                    className="input-element"
                                    value={instructionsInputVal}
                                    onChange={(e) =>
                                        setInstructionsInputVal(e.target.value)
                                    }
                                    style={{ width: "95%" }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            appendCard(
                                                "instructions",
                                                instructionsInputVal,
                                                setInstructionsInputVal
                                            );
                                        }
                                    }}
                                    placeholder={t("inputPlaceholder", {
                                        entity: "instructions",
                                    })}
                                />
                                <button
                                    className={`default-orange-outline-btn self-end ml-2 mb-auto mt-auto ${
                                        "disabled" && ""
                                    }`}
                                    onClick={() =>
                                        appendCard(
                                            "instructions",
                                            instructionsInputVal,
                                            setInstructionsInputVal
                                        )
                                    }
                                >
                                    Add
                                </button>
                            </div>
                            <DraggableProductTags
                                tags={manageModelForm.instructions}
                                setTags={(newTags) => {
                                    setManageModelForm((prevForm) => ({
                                        ...prevForm,
                                        instructions: newTags,
                                    }));
                                }}
                                isColumn={true}
                                removeTagHandler={(instruction, index) =>
                                    removeCard(
                                        index,
                                        "instructions",
                                        setInstructionsInputVal
                                    )
                                }
                                emptyElement={() => <></>}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.instructions}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
    ];

    const versionDetailsFields = [
        {
            element: () => {
                return {
                    label: <Trans i18nKey="versionName">Version Name</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.productVersion && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <input
                                type="text"
                                name="productVersion"
                                onChange={handleInputChange}
                                value={manageModelForm.productVersion}
                                className="input-element"
                                placeholder={t("versionPlaceholder")}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.productVersion}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="versionDescription">
                            Version Description
                        </Trans>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.whatsNew && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <div className="flex mb-1">
                                <input
                                    type="text"
                                    name="whatsNew"
                                    className="input-element"
                                    value={whatsNewInputVal}
                                    onChange={(e) =>
                                        setWhatsNewInputVal(e.target.value)
                                    }
                                    style={{ width: "95%" }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            appendCard(
                                                "whatsNew",
                                                whatsNewInputVal,
                                                setWhatsNewInputVal
                                            );
                                        }
                                    }}
                                    placeholder={t("inputPlaceholder", {
                                        entity: "version updates",
                                    })}
                                />
                                <button
                                    className={`default-orange-outline-btn self-end ml-2 mb-auto mt-auto ${
                                        "disabled" && ""
                                    }`}
                                    onClick={() =>
                                        appendCard(
                                            "whatsNew",
                                            whatsNewInputVal,
                                            setWhatsNewInputVal
                                        )
                                    }
                                >
                                    Add
                                </button>
                            </div>
                            <DraggableProductTags
                                tags={manageModelForm.whatsNew}
                                setTags={(newTags) => {
                                    setManageModelForm((prevForm) => ({
                                        ...prevForm,
                                        whatsNew: newTags,
                                    }));
                                }}
                                isColumn={true}
                                removeTagHandler={(instruction, index) =>
                                    removeCard(
                                        index,
                                        "whatsNew",
                                        setWhatsNewInputVal
                                    )
                                }
                                emptyElement={() => <></>}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.whatsNew}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            hide: !isNewVersion,
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="forceUpgradeProduct">
                            Force Upgrade Product
                        </Trans>
                    ),
                    tooltip: isForceUpgradeEnabled
                        ? t("forceUpgradeTooltip")
                        : t("forceUpgradeDisabledMessage"),
                    input: (
                        <div
                            className={`input-group ${
                                error.productVersion && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="forceUpgrade"
                                options={Object.values(BOOLEAN_DROPDOWN)}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(e, "true");
                                }}
                                checkedReference={
                                    manageModelForm.forceUpgrade?.label ||
                                    BOOLEAN_DROPDOWN.false.label
                                }
                                disabled={!isForceUpgradeEnabled}
                            />
                        </div>
                    ),
                };
            },
        },
        {
            hide: isBundle,
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="enforceOutlier">
                            Enforce Outlier Detection
                        </Trans>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.productVersion && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="enforceOutlierDetection"
                                options={Object.values(BOOLEAN_DROPDOWN)}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(e, "true");
                                }}
                                checkedReference={
                                    manageModelForm.enforceOutlierDetection
                                        ?.label || BOOLEAN_DROPDOWN.false.label
                                }
                            />
                        </div>
                    ),
                };
            },
        },
        {
            element: () => {
                return {
                    label: isBundle ? (
                        <Trans i18nKey="universalBundle">
                            Universal Bundle
                        </Trans>
                    ) : (
                        <Trans i18nKey="universalModel">Universal Model</Trans>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.universalModel && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <VerticalRadioButtons
                                name="universalModel"
                                options={universalModelOptions}
                                onChangeHandler={(e) => {
                                    handleBooleanRadioChange(
                                        e,
                                        MODEL_SCOPE.global
                                    );
                                }}
                                checkedReference={
                                    manageModelForm.universalModel.value
                                }
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.universalModel}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            hide:
                manageModelForm.universalModel.booleanValue === true ||
                manageModelForm.universalModel === "",
            element: () => {
                return {
                    label: (
                        <Trans i18nKey="supportedCountries">
                            Supported Countries
                        </Trans>
                    ),
                    input: (
                        <div
                            className={`input-group ${
                                error.supportedCountries && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <Select
                                isMulti
                                options={countries}
                                onChange={handleMultiSelect}
                                value={manageModelForm.supportedCountries}
                                styles={getReactSelectStyling({
                                    showError:
                                        error.supportedCountries &&
                                        isFormSubmitted,
                                    isMultiSelect: true,
                                })}
                                placeholder={t("selectPlaceholder", {
                                    entity: "Supported Countries",
                                })}
                                className="z-50"
                                name="supportedCountries"
                                getOptionValue={(option) => option.searchable}
                                theme={getReactSelectTheme}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.supportedCountries}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            element: () => {
                return {
                    label: (
                        <div className="flex justify-between">
                            <Trans i18nKey="productGuide">Product Guide</Trans>
                            {productGuide.data && resetLink()}
                        </div>
                    ),
                    input: (
                        <div className="file-upload-wrapper">
                            <input
                                type="file"
                                className="file-upload-input"
                                id="file-input"
                                aria-label="File browser"
                                name="productGuide"
                                onChange={handleFileUpload}
                                accept="application/pdf"
                            />
                            <label
                                className="file-upload-label"
                                htmlFor="file-input"
                            >
                                {productGuide?.name || t("chooseFile")}
                            </label>
                        </div>
                    ),
                    isRequired: false,
                };
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="coverImage">Cover Image</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.coverImage && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <DragDropUploadCard
                                maxFileNumber={1}
                                fileType={IMAGE_TYPE}
                                dimension="350x250"
                                files={coverImage}
                                setFiles={setCoverImage}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={error.coverImage}
                                />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
        {
            element: () => {
                return {
                    label: <Trans i18nKey="images">Images</Trans>,
                    input: (
                        <div
                            className={`input-group ${
                                error.images && isFormSubmitted
                                    ? "invalid-warning-input"
                                    : ""
                            }`}
                        >
                            <DragDropUploadCard
                                maxFileNumber={MAX_FILE_NUMBER}
                                fileType={IMAGE_TYPE}
                                files={productImages}
                                setFiles={setProductImages}
                            />
                            {isFormSubmitted && (
                                <ValidationError errorMessage={error.images} />
                            )}
                        </div>
                    ),
                    isRequired: true,
                };
            },
        },
    ];

    return (
        <>
            <div className="steps step1 show">
                <TitleWithDescription
                    title={t(
                        isBundle ? "publishBundleStep2" : "publishModelStep2"
                    )}
                    description={t(
                        isBundle
                            ? "publishModelStep2Paragraph"
                            : "publishBundleStep2Paragraph"
                    )}
                />
                <div className="card-table shadow flex flex-col w-full">
                    <h3 className="medium-title-bold capitalize">
                        {isBundle ? (
                            <Trans i18nKey="bundleInformation">
                                Bundle Information
                            </Trans>
                        ) : (
                            <Trans i18nKey="productInformation">
                                Product Information
                            </Trans>
                        )}
                    </h3>
                    <hr className="mt-2 mb-5" />
                    {renderFields(modelInformationFields)}
                    <h3 className="medium-title-bold capitalize mt-3">
                        <Trans i18nKey="viewVersions">View Version</Trans>
                    </h3>
                    <hr className="mt-2 mb-5" />
                    {renderFields(versionDetailsFields)}
                </div>
            </div>
            <StepsNavButtons
                back={{ action: prevStep }}
                next={{
                    action: handleNextPage,
                    disabled: isFormSubmitted && !isFormValid,
                    label: nextStepButtonText,
                }}
            />
        </>
    );
};

export default ManageModel;
